import { QueryClient } from "@tanstack/react-query";

import { BidQuery } from "../../generated/app-service-gql/graphql";
import {
  BidNoBidResponse,
  updateBidReportAnswersCache,
} from "../../hooks/api/bid/answers/useGetBidReportAnswers";
import { BidOrchestratorSection } from "../../types/graphQLEnums";
import { updateSectionStatus } from "./bidOrchestratorStatusUtils";
import { BidOrchestratorEntry } from "./types";

const QUALIFICATION_SUMMARY_GROUP_NAME = "Summary";

// Synthetic answerIds. Replaced by the real persisted
// answerId once these sections complete streaming & are hydrated.
const STREAMING_QUALIFICATION_SUMMARY_ID = "__streaming_qualification_summary__";
const STREAMING_WIN_THEME_ID = "__streaming_win_theme__";

/**
 * Handles a streaming chunk for a bid report section that doesn't follow the per-question template
 * structure (qualification_summary and win_themes). Updates BOTH the entry state and the persisted cache in a single branch:
 * - qualification_summary → requirementSummaryReportId cache, under the "Summary" group as a synthetic single question
 * - win_themes → bid query cache, as a synthetic single win theme
 */
export function updateStreamedReportSection({
  nextEntry,
  sectionType,
  eventContent,
  isComplete,
  sources,
  queryClient,
}: {
  nextEntry: BidOrchestratorEntry;
  sectionType: keyof BidOrchestratorEntry["streamingReportSections"];
  eventContent: string;
  isComplete: boolean;
  sources?: BidNoBidResponse[number]["questions"][number]["sources"] | null;
  queryClient: QueryClient;
}): BidOrchestratorEntry {
  const sectionToUpdate = nextEntry.streamingReportSections[sectionType];

  const isNewerContent = !sectionToUpdate || eventContent.length > sectionToUpdate.content.length;
  // Always apply on terminal (isComplete=true) so the section status flips even when the
  // terminal payload is shorter than the last streamed chunk (e.g. the parent workflow
  // publishes an empty array on win-themes failure).
  if (!isNewerContent && !isComplete) {
    return nextEntry;
  }

  // Build a fresh entry rather than mutating `nextEntry` — better for testing
  // and enforces referential identity preservation on no-op updates inside the helper functions.
  let entry: BidOrchestratorEntry = {
    ...nextEntry,
    streamingReportSections: {
      ...nextEntry.streamingReportSections,
      [sectionType]: {
        content: eventContent,
        isComplete,
      },
    },
  };

  // qualification_summary / win_themes have no dedicated *_started or
  // *_success lifecycle events — the only signal we get is the chunk
  // itself. Flip the section status here: first chunk → streaming, chunk
  // with isComplete=true → complete.
  entry = updateSectionStatus(entry, sectionType, isComplete ? "complete" : "streaming");

  // Targeted cache write for this section's destination
  if (sectionType === BidOrchestratorSection.QualificationSummary) {
    const bidReportId = entry.qualificationReportId;
    if (!bidReportId) {
      console.warn(
        "Received qualification_summary chunk before qualificationReportId arrived - skipping cache update",
      );
    } else {
      updateBidReportAnswersCache(queryClient, bidReportId, (prev) => ({
        ...prev,
        result: upsertSummaryGroup(prev.result, eventContent, isComplete ? sources : null),
      }));
    }
  } else if (sectionType === BidOrchestratorSection.WinThemes) {
    let parsedThemes: StreamingPartialWinTheme[] = [];
    try {
      parsedThemes = JSON.parse(eventContent) as StreamingPartialWinTheme[];
    } catch {
      // Mid-stream chunks can be unparseable JSON if the publisher ever ships
      // a partial; the next chunk will carry a complete snapshot.
      return entry;
    }

    queryClient.setQueryData<BidQuery | undefined>(["bid", entry.bidId], (prev) =>
      mergeWinThemesIntoBid(prev, parsedThemes),
    );
  }

  return entry;
}

/**
 * Upsert a single-question "Summary" group into the persisted bid-report
 * shape, carrying the streamed qualification-summary content. If the group
 * already exists we replace its first question; otherwise we append a new
 * group. Terminal-phase hydration replaces this synthetic shape with the
 * persisted version.
 */
function upsertSummaryGroup(
  previousResponse: BidNoBidResponse,
  content: string,
  sources?: BidNoBidResponse[number]["questions"][number]["sources"] | null,
): BidNoBidResponse {
  const summaryQuestion: BidNoBidResponse[number]["questions"][number] = {
    answerId: STREAMING_QUALIFICATION_SUMMARY_ID,
    autoQualify: true,
    title: "",
    order: 0,
    answer: { answer: content },
    sources: sources ?? [],
  };
  const existingIndex = previousResponse.findIndex(
    (g) => g.groupName === QUALIFICATION_SUMMARY_GROUP_NAME,
  );
  if (existingIndex < 0) {
    return [
      ...previousResponse,
      { groupName: QUALIFICATION_SUMMARY_GROUP_NAME, questions: [summaryQuestion] },
    ];
  }
  return previousResponse.map((g, i) =>
    i === existingIndex ? { ...g, questions: [summaryQuestion] } : g,
  );
}

type WinThemeSources = NonNullable<BidQuery["bid"]>["winThemes"][number]["sources"];
type StreamingPartialWinTheme = {
  title?: string;
  description?: string;
  sources?: WinThemeSources;
};

/**
 * Projects the streamed (possibly partial) array of win themes into `bid.winThemes`.
 * IDs are positional (`__streaming_win_theme_<idx>__`) so React keys stay stable as
 * each theme grows.
 */
function mergeWinThemesIntoBid(
  existing: BidQuery | undefined,
  themes: StreamingPartialWinTheme[],
): BidQuery | undefined {
  if (!existing?.bid) return existing;

  // Themes without either a title or description present yet are skipped
  const winThemesToMerge = themes
    .filter((t) => !!t.title || !!t.description)
    .map((t, idx) => ({
      id: `${STREAMING_WIN_THEME_ID}_${idx}`,
      title: t.title ?? "",
      description: t.description ?? "",
      sources: t.sources ?? [],
    }));
  return {
    ...existing,
    bid: {
      ...existing.bid,
      winThemes: winThemesToMerge,
    },
  };
}
